import React from 'react';
import PropTypes from 'prop-types';

import { Row, Column } from 'components/shared/Layout';
import FormattedCurrency from 'components/ui/FormattedCurrency';
import { FormattedMessage } from 'react-intl';
import { jacTravelReferenceRegex, hotelIdRegex } from 'utils/validators';

import {
  StyledErrorMessage,
  StyledInputContainer,
  StyledInputWrapper,
  StyledLabel
} from 'pages/BookingDetails/Extras/ExtrasHotel/AddHotel/AddHotel.styles';

import { SubmitButton, HotelDetailsContainer, StyledPrice, VerticalLine, StyledHr } from '../AddAssistance.styles';
import Input from 'components/ui/Input';

const AddHotelForm = ({ register, values, errors, error, onSubmit, hotelData, hotelDataLoading }) => {
  const shouldRefetchHotel = values.jacTravelRef !== hotelData?.bookingReference;

  return (
    <>
      <StyledInputContainer>
        <StyledInputWrapper>
          <label htmlFor="jacTravelRef">
            <StyledLabel>
              <FormattedMessage id="extras.hotel.modal.jacTravelRef" />
            </StyledLabel>
            <Input
              type="text"
              data-cy="jacTravelRef"
              data-testid="jacTravelRef"
              {...register('jacTravelRef', {
                required: true,
                ...(!hotelData?.isDotW && {
                  pattern: jacTravelReferenceRegex
                })
              })}
            />
          </label>
          <SubmitButton
            type="button"
            onClick={onSubmit}
            disabled={errors?.jacTravelRef || !values.jacTravelRef || hotelDataLoading}
            data-cy="jacTravelRefSubmitButton"
            data-testid="jacTravelRefSubmitButton"
          >
            <FormattedMessage id="paxForm.enter" />
          </SubmitButton>
        </StyledInputWrapper>
        <StyledInputWrapper>
          <label htmlFor="hotelId">
            <StyledLabel>
              <FormattedMessage id="extras.hotel.modal.hotelId" />
            </StyledLabel>
            <Input
              type="text"
              data-cy="hotelId"
              data-testid="hotelId"
              disabled={shouldRefetchHotel || hotelData?.isDotW}
              {...register('hotelId', hotelData?.isDotW ? { required: true } : { required: false, pattern: hotelIdRegex, maxLength: 6 })}
            />
          </label>
        </StyledInputWrapper>
        <StyledInputWrapper>
          <label htmlFor="eurostarPackageRef">
            <StyledLabel>
              <FormattedMessage id="extras.hotel.modal.eurostarPackageRef" />
            </StyledLabel>
            <Input
              type="text"
              data-cy="eurostarPackageRef"
              data-testid="eurostarPackageRef"
              disabled={shouldRefetchHotel || hotelData?.isDotW}
              {...register('eurostarPackageRef', { required: false })}
            />
          </label>
        </StyledInputWrapper>
      </StyledInputContainer>

      {error && (
        <StyledErrorMessage data-testid="errors" data-cy="errors">
          <FormattedMessage id={error} />
        </StyledErrorMessage>
      )}

      {!!hotelData && (
        <HotelDetailsContainer>
          <Row>
            <Column span={4}>
              <VerticalLine>
                <strong>
                  <FormattedMessage id="extras.hotel.modal.passengerName" />
                </strong>
                <div>{hotelData.leadGuestLastName}</div>
              </VerticalLine>
            </Column>
            <Column span={4}>
              <VerticalLine>
                <strong>
                  <FormattedMessage id="extras.hotel.modal.checkInDate" />
                </strong>
                <div>{hotelData.arrivalDate}</div>
              </VerticalLine>
            </Column>
            <Column span={4}>
              <VerticalLine>
                <strong>
                  <FormattedMessage id="extras.hotel.modal.hotelName" />
                </strong>
                <div>{hotelData.hotelName}</div>
              </VerticalLine>
            </Column>
            <Column span={4}>
              <strong>
                <FormattedMessage id="extras.hotel.modal.numberOfNights" />
              </strong>
              <div>{hotelData.duration}</div>
            </Column>
          </Row>
          <StyledHr />
          <Row>
            <Column span={4}>
              <VerticalLine>
                <strong>
                  <FormattedMessage id="extras.hotel.modal.guestCount" />
                </strong>
                <div>{hotelData.numberOfGuests}</div>
              </VerticalLine>
            </Column>
            <Column span={4}>
              <VerticalLine>
                <strong>
                  <FormattedMessage id="extras.hotel.modal.roomCount" />
                </strong>
                <div>{hotelData.numberOfRooms}</div>
              </VerticalLine>
            </Column>
            <Column span={4}>
              <VerticalLine>
                <strong>
                  <FormattedMessage id="extras.hotel.modal.hotelAddress" />
                </strong>
                <div>{hotelData.address}</div>
              </VerticalLine>
            </Column>
          </Row>
          <StyledHr />
          <Row>
            <StyledPrice>
              <strong>
                <FormattedMessage id="extras.hotel.modal.price" />:{' '}
              </strong>
              <FormattedCurrency currency={hotelData.currency} amount={hotelData.priceBeforeMargin} />
            </StyledPrice>
          </Row>
          <Row>
            <StyledPrice>
              <strong>
                <FormattedMessage id="extras.hotel.margin" />:{' '}
              </strong>
              <FormattedCurrency currency={hotelData.currency} amount={hotelData.margin} />
            </StyledPrice>
          </Row>
          <Row>
            <StyledPrice>
              <strong>
                <FormattedMessage id="extras.total" />:{' '}
              </strong>
              <FormattedCurrency currency={hotelData.currency} amount={hotelData.totalPrice} />
            </StyledPrice>
          </Row>
        </HotelDetailsContainer>
      )}
    </>
  );
};

AddHotelForm.propTypes = {
  register: PropTypes.func.isRequired,
  errors: PropTypes.shape({
    jacTravelRef: PropTypes.object,
    hotelId: PropTypes.object
  }),
  error: PropTypes.string,
  values: PropTypes.shape({
    jacTravelRef: PropTypes.string
  }),
  onSubmit: PropTypes.func.isRequired,
  hotelData: PropTypes.shape({
    bookingReference: PropTypes.string,
    totalPrice: PropTypes.number,
    totalCommission: PropTypes.number,
    currency: PropTypes.string,
    productType: PropTypes.string,
    hotelName: PropTypes.string,
    leadGuestLastName: PropTypes.string,
    arrivalDate: PropTypes.string,
    numberOfGuests: PropTypes.number,
    numberOfRooms: PropTypes.number,
    margin: PropTypes.number,
    priceBeforeMargin: PropTypes.number,
    price: PropTypes.number,
    duration: PropTypes.number,
    address: PropTypes.string,
    isDotW: PropTypes.bool
  }),
  hotelDataLoading: PropTypes.bool
};

export default AddHotelForm;
